import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { api } from "@/lib/api";
import { useToast } from "@/components/ui/Toast";
import { TONE_STYLES } from "@/lib/colors";
import { cn, formatDate } from "@/lib/utils";
import type { Webinar } from "@shared/schema";
import type { Segment } from "@shared/welya";
import type {
  PreviewMatch,
  WebinarImportResult,
  WebinarPreview,
  WebinarStats,
} from "@shared/webinar-api";

type Step = "lien" | "verifier" | "importe";

const STEPS: Array<{ key: Step; label: string }> = [
  { key: "lien", label: "1 · Lien" },
  { key: "verifier", label: "2 · Vérifier" },
  { key: "importe", label: "3 · Importé" },
];

const SEGMENTS: Segment[] = ["A", "B", "C", "D", "E"];

const SEGMENT_INFO: Record<
  Segment,
  { label: string; description: string; color: string }
> = {
  A: {
    label: "RDV pris",
    description: "on complète leur fiche (score, % vu, chat), ou on la crée si elle manque",
    color: "#16a34a",
  },
  B: {
    label: "Chauds sans RDV",
    description: "nouvelles fiches, relance perso à J+0",
    color: "var(--color-coral-500)",
  },
  C: {
    label: "Tièdes",
    description: "nouvelles fiches, message à J+1",
    color: "#d97706",
  },
  D: {
    label: "Passage éclair",
    description: "pas de fiche, séquence e-mail de Maxime",
    color: "var(--color-navy-300)",
  },
  E: {
    label: "Absents",
    description: "pas de fiche, séquence e-mail de Maxime",
    color: "var(--color-border-strong)",
  },
};

const DEFAULT_SEGMENTS: Segment[] = ["A", "B", "C"];

function emailKey(email: string): string {
  return email.trim().toLowerCase();
}

function plural(n: number, one: string, many: string): string {
  return n > 1 ? many : one;
}

/** Les lignes qui comptent : ni l'adresse du propriétaire, ni un e-mail répété. */
function isCounted(m: PreviewMatch): boolean {
  return m.method !== "excluded" && m.duplicateRowOf === null;
}

export function WebinarsPage() {
  const toast = useToast();
  const [step, setStep] = useState<Step>("lien");
  const [url, setUrl] = useState("");
  const [csv, setCsv] = useState("");
  const [csvOpen, setCsvOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [preview, setPreview] = useState<WebinarPreview | null>(null);
  const [title, setTitle] = useState("");
  const [fallbackDate, setFallbackDate] = useState("");
  const [ticked, setTicked] = useState<Segment[]>(DEFAULT_SEGMENTS);
  /** Clics de l'utilisateur uniquement : clé = e-mail, id = « C'est elle », null = « Non ». */
  const [choices, setChoices] = useState<Record<string, number | null>>({});
  const [result, setResult] = useState<WebinarImportResult | null>(null);

  const [webinars, setWebinars] = useState<Webinar[] | null>(null);

  // `toast` ne doit pas relancer la requête : on le lit par référence.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const refreshList = useCallback(async () => {
    try {
      const { webinars } = await api.listWebinars();
      setWebinars(webinars);
    } catch (e) {
      toastRef.current.error(e instanceof Error ? e.message : "Erreur");
      setWebinars([]);
    }
  }, []);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const heldAt = preview?.sheet.heldAt ?? (fallbackDate || null);

  async function check() {
    if (!url.trim()) {
      setError("Colle le lien du Google Sheet.");
      return;
    }
    setChecking(true);
    setError(null);
    try {
      const data = await api.previewWebinar({
        url: url.trim(),
        ...(csv.trim() ? { csv } : {}),
      });
      setPreview(data);
      setTitle(data.sheet.title?.trim() || "Webinaire");
      setFallbackDate("");
      setTicked(DEFAULT_SEGMENTS);
      setChoices({});
      setResult(null);
      setStep("verifier");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sheet illisible");
    } finally {
      setChecking(false);
    }
  }

  // Nouvelles fiches (segments cochés, sans fiche existante) et lignes déjà dans le CRM
  // (rapprochées par e-mail, téléphone ou prénom confirmé, quel que soit le segment).
  const counts = useMemo(() => {
    let created = 0;
    let existing = 0;
    if (!preview) return { created, existing };
    for (const m of preview.matches) {
      if (!isCounted(m)) continue;
      const key = emailKey(m.row.email);
      let matched = m.prospectId !== null;
      if (matched && m.method === "name") {
        const effective = key in choices ? choices[key] : m.inheritedConfirmation;
        matched = effective === m.prospectId;
      }
      if (matched) existing++;
      else if (ticked.includes(m.segment)) created++;
    }
    return { created, existing };
  }, [preview, ticked, choices]);

  function toggleSegment(s: Segment) {
    setTicked((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  function choose(m: PreviewMatch, value: number | null) {
    const key = emailKey(m.row.email);
    setChoices((cur) => {
      const next = { ...cur };
      if (key in next && next[key] === value) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  async function doImport() {
    if (importing) return;
    if (!preview || !heldAt) return;
    if (!title.trim()) {
      toast.error("Donne un titre au webinaire");
      return;
    }
    setImporting(true);
    try {
      const r = await api.importWebinar({
        url: url.trim(),
        ...(csv.trim() ? { csv } : {}),
        title: title.trim(),
        heldAt,
        segments: SEGMENTS.filter((s) => ticked.includes(s)),
        confirmations: choices,
      });
      setResult(r);
      setStep("importe");
      refreshList();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur à l'import");
    } finally {
      setImporting(false);
    }
  }

  function restart() {
    setStep("lien");
    setPreview(null);
    setResult(null);
    setChoices({});
    setError(null);
  }

  return (
    <>
      <PageHeader
        title="Importer un webinaire"
        subtitle="Tu colles le lien du Sheet welya, le CRM lit l'export et te montre ce qu'il va faire avant d'écrire quoi que ce soit."
      />

      <Steps current={step} />

      {step === "lien" && (
        <section className="surface p-5 max-w-3xl">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              check();
            }}
            className="space-y-4"
          >
            <label className="block">
              <span className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
                Lien du Google Sheet
              </span>
              <input
                className="input-base mt-1.5"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/…/edit"
                autoFocus
              />
            </label>

            {import.meta.env.DEV && (
              <div className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-border-strong)] p-3">
                <button
                  type="button"
                  className="flex items-center gap-1.5 text-sm font-medium text-[var(--color-navy-700)]"
                  onClick={() => setCsvOpen((v) => !v)}
                  aria-expanded={csvOpen}
                >
                  {csvOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Coller le CSV (dev)
                </button>
                {csvOpen && (
                  <textarea
                    className="input-base mt-2 font-mono text-xs"
                    rows={6}
                    value={csv}
                    onChange={(e) => setCsv(e.target.value)}
                    placeholder="Contenu CSV du Sheet, à la place du téléchargement"
                    aria-label="CSV du Sheet (dev)"
                  />
                )}
              </div>
            )}

            {error && (
              <div className="rounded-[var(--radius-md)] border border-[var(--color-coral-200)] bg-[var(--color-coral-50)] p-3 text-sm text-[var(--color-coral-700)]">
                {error}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button type="submit" className="btn btn-primary" disabled={checking}>
                {checking && <Loader2 size={14} className="animate-spin" />}
                {checking ? "Lecture du Sheet…" : "Vérifier"}
              </button>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                Cette étape n'écrit rien dans le CRM.
              </span>
            </div>
          </form>
        </section>
      )}

      {step === "verifier" && preview && (
        <Verify
          url={url}
          preview={preview}
          title={title}
          onTitle={setTitle}
          heldAt={heldAt}
          onFallbackDate={setFallbackDate}
          ticked={ticked}
          onToggle={toggleSegment}
          choices={choices}
          onChoose={choose}
          counts={counts}
          importing={importing}
          onBack={() => setStep("lien")}
          onImport={doImport}
        />
      )}

      {step === "importe" && result && (
        <Done result={result} title={title} onRestart={restart} />
      )}

      <ImportedList webinars={webinars} />
    </>
  );
}

function Steps({ current }: { current: Step }) {
  const index = STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="mb-5 flex flex-wrap gap-2" aria-label="Étapes">
      {STEPS.map((s, i) => (
        <li
          key={s.key}
          aria-current={i === index ? "step" : undefined}
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold border",
            i === index
              ? "bg-[var(--color-navy-600)] text-white border-[var(--color-navy-700)]"
              : i < index
                ? "bg-[var(--color-navy-50)] text-[var(--color-navy-700)] border-[var(--color-navy-200)]"
                : "bg-white text-[var(--color-muted-foreground)] border-[var(--color-border)]",
          )}
        >
          {s.label}
        </li>
      ))}
    </ol>
  );
}

function Verify({
  url,
  preview,
  title,
  onTitle,
  heldAt,
  onFallbackDate,
  ticked,
  onToggle,
  choices,
  onChoose,
  counts,
  importing,
  onBack,
  onImport,
}: {
  url: string;
  preview: WebinarPreview;
  title: string;
  onTitle: (v: string) => void;
  heldAt: string | null;
  onFallbackDate: (v: string) => void;
  ticked: Segment[];
  onToggle: (s: Segment) => void;
  choices: Record<string, number | null>;
  onChoose: (m: PreviewMatch, value: number | null) => void;
  counts: { created: number; existing: number };
  importing: boolean;
  onBack: () => void;
  onImport: () => void;
}) {
  const { stats, matches, alerts } = preview;
  const hasSheetDate = preview.sheet.heldAt !== null;

  const summary = useMemo(() => {
    const counted = matches.filter(isCounted);
    return {
      email: counted.filter((m) => m.method === "email"),
      phone: counted.filter((m) => m.method === "phone"),
      toConfirm: counted.filter((m) => m.method === "name" && m.needsConfirmation),
      duplicates: counted.filter(
        (m) => m.method === "new" && m.possibleDuplicateOf !== null,
      ),
      excluded: matches.filter((m) => m.method === "excluded"),
      repeated: matches.filter((m) => m.duplicateRowOf !== null),
    };
  }, [matches]);

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-start">
      <div className="space-y-4">
        <div className="surface p-4 space-y-3">
          <div>
            <div className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
              Lien du Google Sheet
            </div>
            <div className="mt-1 text-sm text-[var(--color-navy-700)] break-all">{url}</div>
          </div>
          <div className="grid gap-3 sm:grid-cols-[9rem_minmax(0,1fr)]">
            <div>
              <div className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
                Date du live
              </div>
              {hasSheetDate ? (
                <div
                  className="mt-1.5 h-10 flex items-center text-sm font-medium tabular text-[var(--color-navy-700)]"
                  title="La date vient du Sheet, elle ne se modifie pas"
                >
                  {formatDate(heldAt)}
                </div>
              ) : (
                <input
                  type="date"
                  className="input-base mt-1.5"
                  value={heldAt ?? ""}
                  onChange={(e) => onFallbackDate(e.target.value)}
                  aria-label="Date du live"
                />
              )}
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
                Titre du webinaire
              </span>
              <input
                className="input-base mt-1.5"
                value={title}
                onChange={(e) => onTitle(e.target.value)}
              />
            </label>
          </div>
          {!hasSheetDate && (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Le Sheet n'indique pas de date : choisis celle du live.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">
            Ce qui sera créé
          </h2>
          <span className="text-sm text-[var(--color-muted-foreground)] tabular">
            {stats.inscrits} inscrits · {stats.presents} présents · {stats.rdv} RDV
          </span>
        </div>

        <div className="surface divide-y divide-[var(--color-border)]">
          {SEGMENTS.map((s) => (
            <SegmentRow
              key={s}
              segment={s}
              stats={stats}
              on={ticked.includes(s)}
              disabled={importing}
              onToggle={() => onToggle(s)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">
          Rapprochement avec tes prospects
        </h2>
        <div className="surface p-4 space-y-3">
          <ul className="space-y-2.5 text-sm">
            <li>
              <b className="tabular">{summary.email.length}</b> retrouvé
              {plural(summary.email.length, "", "s")} par e-mail
            </li>
            {summary.phone.length > 0 && (
              <li>
                <b className="tabular">{summary.phone.length}</b> par téléphone :{" "}
                {summary.phone.map((m) => m.row.firstName || m.row.email).join(", ")}{" "}
                <span className="text-[var(--color-muted-foreground)]">
                  (autre e-mail au webinaire)
                </span>
              </li>
            )}
            {summary.toConfirm.map((m) => (
              <ConfirmRow
                key={emailKey(m.row.email)}
                match={m}
                choice={choices[emailKey(m.row.email)]}
                hasChoice={emailKey(m.row.email) in choices}
                onChoose={onChoose}
              />
            ))}
            {summary.duplicates.map((m) => (
              <li key={emailKey(m.row.email)}>
                <span className="font-medium">{m.row.firstName || m.row.email}</span> : nouvelle
                fiche, mais ressemble à une fiche existante{" "}
                <Link
                  to={`/prospects/${m.possibleDuplicateOf}`}
                  target="_blank"
                  className="inline-flex items-center gap-1 text-[var(--color-navy-600)] underline"
                >
                  voir <ExternalLink size={11} />
                </Link>
              </li>
            ))}
            {summary.excluded.length > 0 && (
              <li>
                <b className="tabular">{summary.excluded.length}</b> exclu
                {plural(summary.excluded.length, "", "s")} : toi (
                {summary.excluded.map((m) => m.row.email).join(", ")})
              </li>
            )}
            {summary.repeated.length > 0 && (
              <li>
                <b className="tabular">{summary.repeated.length}</b> ligne
                {plural(summary.repeated.length, "", "s")} avec un e-mail répété, ignorée
                {plural(summary.repeated.length, "", "s")}
              </li>
            )}
          </ul>

          {alerts.length > 0 && <Alerts alerts={alerts} />}
        </div>

        <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">Ensuite</h2>
        <div className="surface p-4 text-sm text-[var(--color-muted-foreground)] leading-relaxed">
          Les fiches arrivent avec leur segment, leur prochaine action datée et leur message
          WhatsApp prêt. Elles apparaissent dans « Aujourd'hui ».
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            className="btn btn-outline"
            onClick={onBack}
            disabled={importing}
          >
            Retour
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onImport}
            disabled={importing || !heldAt || !title.trim()}
          >
            {importing && <Loader2 size={14} className="animate-spin" />}
            Importer : {counts.created} {plural(counts.created, "nouvelle", "nouvelles")},{" "}
            {counts.existing} déjà dans le CRM
          </button>
        </div>
      </div>
    </div>
  );
}

function SegmentRow({
  segment,
  stats,
  on,
  disabled,
  onToggle,
}: {
  segment: Segment;
  stats: WebinarStats;
  on: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const info = SEGMENT_INFO[segment];
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span
        className="h-3 w-3 rounded-full flex-shrink-0"
        style={{ background: info.color }}
        aria-hidden
      />
      <span className="w-8 text-right text-lg font-bold tabular text-[var(--color-navy-700)]">
        {stats.segments[segment]}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[var(--color-navy-700)]">
          {segment} · {info.label}
        </div>
        <div className="text-xs text-[var(--color-muted-foreground)]">{info.description}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        aria-label={`Importer le segment ${segment} · ${info.label}`}
        onClick={onToggle}
        className={cn(
          "relative h-6 w-11 flex-shrink-0 rounded-full border transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
          on
            ? "bg-[var(--color-navy-600)] border-[var(--color-navy-700)]"
            : "bg-[var(--color-muted)] border-[var(--color-border-strong)]",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-4.5 w-4.5 rounded-full bg-white shadow transition-all",
            on ? "left-[22px]" : "left-0.5",
          )}
        />
      </button>
    </div>
  );
}

function ConfirmRow({
  match,
  choice,
  hasChoice,
  onChoose,
}: {
  match: PreviewMatch;
  choice: number | null | undefined;
  hasChoice: boolean;
  onChoose: (m: PreviewMatch, value: number | null) => void;
}) {
  const inherited = match.inheritedConfirmation !== null && !hasChoice;
  const yes = hasChoice ? choice === match.prospectId : inherited;
  const no = hasChoice && choice === null;
  const name = match.row.firstName || match.row.email;
  const green = TONE_STYLES.green;
  return (
    <li className="rounded-[var(--radius-md)] bg-[var(--color-navy-50)] border border-[var(--color-navy-100)] p-3">
      <div>
        <b className="tabular">1</b> par prénom, {inherited ? "déjà confirmé" : "à confirmer"} :{" "}
        <span className="font-medium">{name}</span>{" "}
        <span className="text-[var(--color-muted-foreground)]">
          ({match.row.email}, e-mail et téléphone différents)
        </span>
        {match.prospectId !== null && (
          <>
            {" "}
            <Link
              to={`/prospects/${match.prospectId}`}
              target="_blank"
              className="inline-flex items-center gap-1 text-[var(--color-navy-600)] underline"
            >
              voir la fiche <ExternalLink size={11} />
            </Link>
          </>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {inherited ? (
          <span className={cn("pill", green.pillBg, green.pillFg)}>
            <Check size={12} /> Déjà confirmé
          </span>
        ) : (
          <button
            type="button"
            className={cn("btn btn-sm", yes ? "btn-success" : "btn-outline")}
            aria-pressed={yes}
            onClick={() => onChoose(match, match.prospectId)}
          >
            {yes && <Check size={13} />} C'est elle / lui
          </button>
        )}
        <button
          type="button"
          className={cn("btn btn-sm", no ? "btn-primary" : "btn-ghost")}
          aria-pressed={no}
          onClick={() => onChoose(match, null)}
        >
          Non
        </button>
        {!yes && !no && !inherited && (
          <span className="text-xs text-[var(--color-muted-foreground)]">
            Sans clic, une nouvelle fiche sera créée.
          </span>
        )}
      </div>
    </li>
  );
}

function Alerts({ alerts }: { alerts: string[] }) {
  const tone = TONE_STYLES.yellow;
  return (
    <div
      role="status"
      className={cn(
        "rounded-[var(--radius-md)] border p-3 text-sm",
        tone.cardBg,
        tone.cardBorder,
        tone.pillFg,
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
        <ul className="space-y-1">
          {alerts.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Done({
  result,
  title,
  onRestart,
}: {
  result: WebinarImportResult;
  title: string;
  onRestart: () => void;
}) {
  const green = TONE_STYLES.green;
  return (
    <section className="surface p-6 max-w-3xl">
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "h-9 w-9 rounded-full grid place-items-center flex-shrink-0",
            green.pillBg,
            green.pillFg,
          )}
        >
          <Check size={18} />
        </span>
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">Webinaire importé</h2>
          <p className="text-sm text-[var(--color-muted-foreground)]">{title}</p>
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-3 gap-3">
        <ResultCell label={plural(result.created, "Fiche créée", "Fiches créées")} value={result.created} />
        <ResultCell
          label={plural(result.completed, "Fiche complétée", "Fiches complétées")}
          value={result.completed}
        />
        <ResultCell
          label={plural(result.skipped, "Ligne ignorée", "Lignes ignorées")}
          value={result.skipped}
        />
      </dl>

      <div className="mt-6 flex flex-wrap gap-2">
        <Link to="/aujourdhui" className="btn btn-primary">
          Voir Aujourd'hui
        </Link>
        <Link to="/prospects" className="btn btn-outline">
          Voir les prospects
        </Link>
        <button type="button" className="btn btn-ghost" onClick={onRestart}>
          Importer un autre webinaire
        </button>
      </div>
    </section>
  );
}

function ResultCell({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-[var(--radius-md)] bg-[var(--color-navy-50)] border border-[var(--color-navy-100)] p-3">
      <dd className="text-2xl font-bold tabular text-[var(--color-navy-700)]">{value}</dd>
      <dt className="text-xs text-[var(--color-muted-foreground)]">{label}</dt>
    </div>
  );
}

function parseStats(json: string | null): Partial<WebinarStats> {
  if (!json) return {};
  try {
    return JSON.parse(json) as Partial<WebinarStats>;
  } catch {
    return {};
  }
}

function ImportedList({ webinars }: { webinars: Webinar[] | null }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-[var(--color-navy-700)] mb-3">
        Webinaires importés
      </h2>
      {webinars === null ? (
        <div className="surface p-6 text-center text-sm text-[var(--color-muted-foreground)]">
          Chargement…
        </div>
      ) : webinars.length === 0 ? (
        <div className="surface p-6 text-center text-sm text-[var(--color-muted-foreground)]">
          Aucun webinaire importé pour l'instant.
        </div>
      ) : (
        <>
        <ul className="sm:hidden space-y-2">
          {webinars.map((w) => {
            const s = parseStats(w.statsJson);
            const parts = (
              [
                ["Inscrits", s.inscrits],
                ["Présents", s.presents],
                ["RDV", s.rdv],
              ] as const
            )
              .filter(([, n]) => typeof n === "number")
              .map(([label, n]) => `${label} ${n}`);
            return (
              <li key={w.id} className="surface p-3">
                <div className="font-medium text-[var(--color-navy-700)]">
                  {w.title}
                </div>
                <div className="mt-0.5 text-xs tabular text-[var(--color-muted-foreground)]">
                  {formatDate(w.heldAt)}
                </div>
                {parts.length > 0 && (
                  <div className="mt-1.5 text-sm tabular">
                    {parts.join(" · ")}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className="surface overflow-x-auto hidden sm:block">
          <table className="table-base">
            <thead>
              <tr>
                <th>Date</th>
                <th>Titre</th>
                <th className="text-right">Inscrits</th>
                <th className="text-right">Présents</th>
                <th className="text-right">RDV</th>
              </tr>
            </thead>
            <tbody>
              {webinars.map((w) => {
                const s = parseStats(w.statsJson);
                const num = (n: number | undefined) => (typeof n === "number" ? n : "");
                return (
                  <tr key={w.id}>
                    <td className="tabular whitespace-nowrap">{formatDate(w.heldAt)}</td>
                    <td className="font-medium text-[var(--color-navy-700)]">{w.title}</td>
                    <td className="text-right tabular">{num(s.inscrits)}</td>
                    <td className="text-right tabular">{num(s.presents)}</td>
                    <td className="text-right tabular">{num(s.rdv)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </section>
  );
}
